import type { ReactNode } from "react";
import { memo, useCallback, useEffect, useRef } from "react";

import type { TimelineItem } from "@/hooks/useChat";

interface ChatConversationViewProps {
  timeline: TimelineItem[];
  children: ReactNode;
  workingIndicator?: ReactNode;
  errorBanner?: ReactNode;
  statusBanners: ReactNode;
  chatInput: ReactNode;
}

interface ChatTimelineProps {
  children: ReactNode;
  workingIndicator?: ReactNode;
  errorBanner?: ReactNode;
  timeline: TimelineItem[];
}

const SCROLL_THRESHOLD = 80;

const ChatTimeline = memo(function ChatTimeline(props: ChatTimelineProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const prevTimelineLenRef = useRef(0);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    isNearBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD;
  }, []);

  useEffect(() => {
    const newUserMessage =
      props.timeline.length > prevTimelineLenRef.current &&
      props.timeline[prevTimelineLenRef.current]?.kind === "user";
    prevTimelineLenRef.current = props.timeline.length;

    if (newUserMessage) {
      scrollToBottom();
    } else if (isNearBottomRef.current) {
      scrollToBottom();
    }
  }, [props.timeline, scrollToBottom]);

  return (
    <div
      ref={scrollContainerRef}
      onScroll={handleScroll}
      className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pt-5"
    >
      <div className="mx-auto max-w-2xl space-y-4 pb-4">
        {props.children}

        {props.workingIndicator}

        {props.errorBanner}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
});

export const ChatConversationView = (props: ChatConversationViewProps) => (
  <>
    <ChatTimeline
      timeline={props.timeline}
      workingIndicator={props.workingIndicator}
      errorBanner={props.errorBanner}
    >
      {props.children}
    </ChatTimeline>

    {props.statusBanners}

    {props.chatInput}
  </>
);
